"use client";

import { useCallback } from "react";
import { validateFields } from "@/lib/utils";
import { toast } from "@/hooks/useToast";
import { Section, FormState, TreeConfig, TreeItem } from "../types";
import { VariantConfig } from "./types";
import {
  extractChildItemsFromResponse,
  buildDefaultFieldValues,
  mapItemToFormFields,
  createTempId,
  prepareSectionDataForPreview,
} from "../utils";

interface UseTreeItemActionsProps {
  selectedSection: Section;
  formState: FormState;
  setFormState: React.Dispatch<React.SetStateAction<FormState>>;
  treeConfig?: TreeConfig;
  componentFieldsConfig: any[];
  fieldsConfig: any[];
  currentVariantDetails: VariantConfig | null;
  fetchMethod?: (id: number) => Promise<any>;
  deleteMethod?: (id: number) => Promise<any>;
  currentUserId?: string | number;
  pageId?: string;
  pageComponentId?: string;
  reduxState: any;
  dispatch: any;
  fetchTreeItems: () => Promise<void>;
}

// ============================================================================
// Toast Helpers
// ============================================================================

const showErrorToast = (description: string) => {
  toast({ title: "Error", description, variant: "destructive" });
};

const showSuccessToast = (description: string) => {
  toast({ title: "Success", description });
};

const showValidationErrorToast = () => {
  toast({
    title: "Validation Error",
    description: "Please fix the errors before saving",
    variant: "destructive",
  });
};

// ============================================================================
// Count Helpers
// ============================================================================

/** Count top-level tree items (those with no tree parent) — e.g. nav links. */
const countTopLevelItems = (formState: FormState, treeConfig?: TreeConfig): number => {
  const parentIdField = treeConfig?.fieldMappings?.parentId || "parent_id";
  return formState.treeItems.filter((it: any) => {
    const p = it[parentIdField];
    return p == null || p === "" || p === "null";
  }).length;
};

// ============================================================================
// Payload Helpers
// ============================================================================

const buildSavePayload = (
  formState: FormState,
  treeConfig: TreeConfig | undefined,
  currentUserId: string | number | undefined,
  isUpdate: boolean
): any => {
  const { id, ...fieldsWithoutId } = formState.componentFields;
  const parentField = treeConfig?.parentFieldName;

  const payload: any = {
    ...(isUpdate ? { id: formState.editingItemId } : {}),
    ...fieldsWithoutId,
    ...(isUpdate ? { updated_by: currentUserId } : { created_by: currentUserId }),
  };

  // Normalize top-level parent value to null
  if (parentField && payload[parentField] === (treeConfig?.parentTopLevelValue || "null")) {
    payload[parentField] = null;
  }

  return payload;
};

const getParentComponentId = (
  selectedSection: Section,
  formState: FormState
): string | number | undefined => {
  return (
    selectedSection?.componentId ||
    selectedSection?.data?.id ||
    formState.parentComponentId ||
    undefined
  );
};

// ============================================================================
// Preview Update Helper
// ============================================================================

const updatePreview = async (
  parentComponentId: string | number,
  fetchMethod: (id: number) => Promise<any>,
  treeConfig: TreeConfig | undefined,
  selectedSection: Section,
  formState: FormState,
  pageId: string | undefined,
  pageComponentId: string | undefined,
  onSave: (updatedSection: Section) => void
) => {
  const childItemsFieldName = treeConfig?.childItemsFieldName || "menuItems";
  const response = await fetchMethod(Number(parentComponentId));
  const { items, parentComponentData } = extractChildItemsFromResponse(response, childItemsFieldName);

  onSave(
    prepareSectionDataForPreview(
      selectedSection,
      parentComponentId,
      parentComponentData,
      formState.parentComponentFields,
      items,
      childItemsFieldName,
      pageId,
      pageComponentId
    )
  );
};

// ============================================================================
// Form State Helpers
// ============================================================================

const resetFormToDefaults = (
  setFormState: React.Dispatch<React.SetStateAction<FormState>>,
  componentFieldsConfig: any[]
) => {
  const defaults = buildDefaultFieldValues(componentFieldsConfig);
  defaults.id = createTempId();

  setFormState((prev) => ({
    ...prev,
    componentFields: defaults,
    editingItemId: null,
    componentFieldErrors: {},
    componentFieldMessages: {},
  }));
};

const clearEditingState = (
  setFormState: React.Dispatch<React.SetStateAction<FormState>>,
  componentFieldsConfig: any[]
) => {
  const defaults = buildDefaultFieldValues(componentFieldsConfig);
  setFormState((prev) => ({
    ...prev,
    componentFields: defaults,
    editingItemId: null,
  }));
};

// ============================================================================
// Main Hook
// ============================================================================

export const useTreeItemActions = ({
  selectedSection,
  formState,
  setFormState,
  treeConfig,
  componentFieldsConfig,
  fieldsConfig,
  currentVariantDetails,
  fetchMethod,
  deleteMethod,
  currentUserId,
  pageId,
  pageComponentId,
  reduxState,
  dispatch,
  fetchTreeItems,
}: UseTreeItemActionsProps) => {
  // ---------------------------------------------------------------------------
  // Field Update Handler
  // ---------------------------------------------------------------------------
  const handleComponentFieldsUpdate = useCallback(
    (updatedData: Record<string, any>) => {
      const parentField = treeConfig?.parentFieldName;
      if (parentField && updatedData[parentField] === (treeConfig?.parentTopLevelValue || "null")) {
        updatedData[parentField] = null;
      }

      setFormState((prev) => ({ ...prev, componentFields: updatedData }));

      if (currentVariantDetails?.setAction) {
        dispatch(
          currentVariantDetails.setAction({
            ...reduxState,
            ...updatedData,
            id: reduxState?.id || "",
            is_active: updatedData.is_active ?? (reduxState as any)?.is_active ?? true,
          } as any)
        );
      }
    },
    [currentVariantDetails, dispatch, reduxState, treeConfig, setFormState]
  );

  // ---------------------------------------------------------------------------
  // Save Handler
  // ---------------------------------------------------------------------------
  const handleSave = useCallback(
    async (onSave: (updatedSection: Section) => void) => {
      // Guard: Check config exists
      if (!currentVariantDetails) {
        showErrorToast("Widget configuration not found");
        return;
      }

      // Validate fields
      const { errors, messages } = validateFields(fieldsConfig, formState.componentFields);
      setFormState((prev) => ({
        ...prev,
        componentFieldErrors: errors,
        componentFieldMessages: messages,
      }));

      if (Object.keys(errors).length > 0) {
        showValidationErrorToast();
        return;
      }

      // Get parent component ID
      const parentComponentId = getParentComponentId(selectedSection, formState);
      if (!parentComponentId || isNaN(Number(parentComponentId))) {
        showErrorToast(
          treeConfig?.requireParentMessage ||
            "Please save the parent component settings first before creating child items."
        );
        return;
      }

      const isUpdate = formState.editingItemId && !String(formState.editingItemId).startsWith("temp_");

      // Enforce top-level item count limit (e.g. nav links: max). Only applies
      // when adding a NEW top-level item and the variant declares itemLimits.
      const limits = treeConfig?.itemLimits;
      if (!isUpdate && limits?.max != null) {
        const parentField = treeConfig?.parentFieldName || "parent_id";
        const topVal = treeConfig?.parentTopLevelValue ?? "null";
        const newParent = formState.componentFields[parentField];
        const addingTopLevel =
          newParent == null || newParent === "" || newParent === topVal || newParent === "null";
        if (addingTopLevel && countTopLevelItems(formState, treeConfig) >= limits.max) {
          showErrorToast(`You can add a maximum of ${limits.max} nav links.`);
          return;
        }
      }

      try {
        const payload = buildSavePayload(formState, treeConfig, currentUserId, Boolean(isUpdate));
        payload[treeConfig?.parentIdFieldName || "parent_component_id"] = Number(parentComponentId);

        // Execute save operation
        if (isUpdate) {
          if (!currentVariantDetails.updateMethod) throw new Error("Update method not configured");
          await currentVariantDetails.updateMethod(Number(formState.editingItemId), payload);
        } else {
          if (!currentVariantDetails.createMethod) throw new Error("Create method not configured");
          const response = await currentVariantDetails.createMethod(payload);
          if (!response?.data?.id && !response?.id) throw new Error("Failed to get ID from response");
        }

        showSuccessToast(`Item ${isUpdate ? "updated" : "created"} successfully`);
        resetFormToDefaults(setFormState, componentFieldsConfig);
        await fetchTreeItems();

        // Update preview
        if (fetchMethod) {
          await updatePreview(
            parentComponentId,
            fetchMethod,
            treeConfig,
            selectedSection,
            formState,
            pageId,
            pageComponentId,
            onSave
          );
        }
      } catch (error: any) {
        console.error("Failed to save:", error);
        showErrorToast(error.message || "Failed to save item");
      }
    },
    [
      currentVariantDetails,
      fieldsConfig,
      formState,
      treeConfig,
      currentUserId,
      selectedSection,
      componentFieldsConfig,
      fetchTreeItems,
      fetchMethod,
      pageId,
      pageComponentId,
      setFormState,
    ]
  );

  // ---------------------------------------------------------------------------
  // Edit Handler
  // ---------------------------------------------------------------------------
  const handleEdit = useCallback(
    (item: TreeItem) => {
      if (!treeConfig) return;

      const formData = mapItemToFormFields(item, componentFieldsConfig, treeConfig);
      const idField = treeConfig.fieldMappings?.id || "id";

      setFormState((prev) => ({
        ...prev,
        componentFields: formData,
        editingItemId: String(item[idField]),
        componentFieldErrors: {},
        componentFieldMessages: {},
      }));
    },
    [componentFieldsConfig, treeConfig, setFormState]
  );

  // ---------------------------------------------------------------------------
  // Delete Handler
  // ---------------------------------------------------------------------------
  const handleDelete = useCallback(
    async (item: TreeItem, onSave: (updatedSection: Section) => void) => {
      // Guard: Check required dependencies
      if (!deleteMethod || !treeConfig) {
        showErrorToast("Delete method not configured");
        return;
      }

      // Get field mappings
      const idField = treeConfig.fieldMappings?.id || "id";
      const itemId = item[idField];

      // Note: Confirmation should be handled by caller using DeleteDialog
      try {
        await deleteMethod(Number(itemId));
        showSuccessToast("Item deleted successfully");

        // Clear form if deleting currently edited item
        if (formState.editingItemId === String(itemId)) {
          clearEditingState(setFormState, componentFieldsConfig);
        }

        await fetchTreeItems();

        // Update preview
        const parentId = selectedSection?.componentId || formState.parentComponentId;
        if (parentId && fetchMethod) {
          await updatePreview(
            parentId,
            fetchMethod,
            treeConfig,
            selectedSection,
            formState,
            pageId,
            pageComponentId,
            onSave
          );
        }
      } catch (error) {
        console.error("Failed to delete:", error);
        showErrorToast("Failed to delete item");
      }
    },
    [
      deleteMethod,
      treeConfig,
      formState,
      componentFieldsConfig,
      fetchTreeItems,
      selectedSection,
      fetchMethod,
      pageId,
      pageComponentId,
      setFormState,
    ]
  );

  // ---------------------------------------------------------------------------
  // New Item Handler
  // ---------------------------------------------------------------------------
  const handleNewItem = useCallback(() => {
    resetFormToDefaults(setFormState, componentFieldsConfig);
  }, [componentFieldsConfig, setFormState]);

  // ---------------------------------------------------------------------------
  // Toggle Expand Handler
  // ---------------------------------------------------------------------------
  const toggleExpand = useCallback(
    (id: string) => {
      setFormState((prev) => {
        const newSet = new Set(prev.expandedIds);
        newSet.has(id) ? newSet.delete(id) : newSet.add(id);
        return { ...prev, expandedIds: newSet };
      });
    },
    [setFormState]
  );

  return {
    handleComponentFieldsUpdate,
    handleSave,
    handleEdit,
    handleDelete,
    handleNewItem,
    toggleExpand,
  };
};
